import { useState } from "react";
import { curMonth, buildChecklist, uid } from "../utils";
import { Card, Btn, Inp, BlockHead, KpiStrip, MiniBar, Check } from "./primitives";

export function PlanoTab({ client, planos, onNewPlano, onUpdatePlano, onDeletePlano }) {
  const month = curMonth();
  const [generating, setGenerating] = useState(false);
  const [addingItem, setAddingItem] = useState(null);
  const [newItemText, setNewItemText] = useState("");

  const planosDoMes = planos.filter((p) => p.month === month);
  const planoAtual  = planosDoMes[0] || null;
  const planosAnteriores = planos.filter((p) => p.month !== month);

  async function handleGerar() {
    setGenerating(true);
    const items = buildChecklist(client.notes || "", client.type || "");
    await onNewPlano({
      client_id: client.id,
      month,
      title: `Plano Operacional — ${month}`,
      items,
    });
    setGenerating(false);
  }

  async function toggleItem(plano, itemId) {
    const updated = plano.items.map((i) =>
      i.id === itemId ? { ...i, done: !i.done } : i
    );
    await onUpdatePlano(plano.id, { items: updated });
  }

  async function addCustomItem(plano, group) {
    if (!newItemText.trim()) return;
    const updated = [...plano.items, { id: uid(), group, text: newItemText.trim(), done: false }];
    await onUpdatePlano(plano.id, { items: updated });
    setNewItemText("");
    setAddingItem(null);
  }

  async function removeItem(plano, itemId) {
    const updated = plano.items.filter((i) => i.id !== itemId);
    await onUpdatePlano(plano.id, { items: updated });
  }

  const groups = planoAtual
    ? [...new Set(planoAtual.items.map((i) => i.group))]
    : [];

  const done  = planoAtual ? planoAtual.items.filter((i) => i.done).length : 0;
  const total = planoAtual ? planoAtual.items.length : 0;
  const pct   = total ? Math.round((done / total) * 100) : 0;

  const anteriores = planosAnteriores.length > 0 && (
    <div className="klist" style={{ marginTop: 16 }}>
      <div className="klist-head"><span className="eyebrow">Planos anteriores</span></div>
      {planosAnteriores.map((p) => (
        <div key={p.id} className="klist-row">
          <span className="txt" style={{ color: "var(--muted)" }}>{p.title}</span>
          <MiniBar done={p.items.filter((i) => i.done).length} total={p.items.length} width={70} />
        </div>
      ))}
    </div>
  );

  if (!planoAtual) {
    return (
      <div className="tab-pane">
        <BlockHead title={`Plano de ${month}`} sub="Nenhum plano ainda. Ele sai das plataformas do cliente." />
        <Card style={{ padding: 28, textAlign: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Nenhum plano para {month}</div>
          <div style={{ fontSize: 13.5, color: "var(--muted)", marginBottom: 18 }}>
            Gere o plano operacional do mês com base nas plataformas do cliente.
          </div>
          <Btn variant="primary" onClick={handleGerar} disabled={generating}>
            {generating ? "Gerando..." : "Gerar plano do mês"}
          </Btn>
        </Card>
        {anteriores}
      </div>
    );
  }

  return (
    <div className="tab-pane">
      <BlockHead title={planoAtual.title} sub="Marque o que foi feito. Cada grupo mostra quanto falta.">
        <Btn sm variant="ghost" onClick={() => onDeletePlano(planoAtual.id)}>Excluir plano</Btn>
      </BlockHead>

      <KpiStrip style={{ marginBottom: 16 }} items={[
        { label: "Concluído", value: `${pct}%`, sub: pct === 100 ? "plano fechado" : pct >= 50 ? "no ritmo" : "abaixo de 50%", tone: pct === 100 ? "ok" : pct >= 50 ? "" : "warn" },
        { label: "Itens feitos", value: `${done}/${total}` },
        { label: "Grupos", value: groups.length, sub: `${groups.filter((g) => planoAtual.items.filter((i) => i.group === g).every((i) => i.done)).length} completos` },
      ]} />

      <div style={{ display: "grid", gap: 12 }}>
        {groups.map((group) => {
          const groupItems = planoAtual.items.filter((i) => i.group === group);
          const groupDone  = groupItems.filter((i) => i.done).length;
          return (
            <div key={group} className="klist">
              <div className="klist-head">
                <span className="eyebrow">{group}</span>
                <MiniBar done={groupDone} total={groupItems.length} width={70} />
              </div>
              {groupItems.map((item) => (
                <div key={item.id} className={`klist-row${item.done ? " done" : ""}`}>
                  <Check on={item.done} onClick={() => toggleItem(planoAtual, item.id)} label={item.text} />
                  <span className="txt">{item.text}</span>
                  <button
                    type="button"
                    aria-label="Remover item"
                    onClick={() => removeItem(planoAtual, item.id)}
                    style={{ background: "none", border: "none", color: "var(--faint)", cursor: "pointer", fontSize: 15, padding: "0 4px" }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="klist-foot">
                {addingItem === group ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Inp
                      value={newItemText}
                      onChange={(e) => setNewItemText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addCustomItem(planoAtual, group)}
                      placeholder="Descreva a ação..."
                      style={{ flex: 1 }}
                      autoFocus
                    />
                    <Btn sm variant="primary" onClick={() => addCustomItem(planoAtual, group)}>Adicionar</Btn>
                    <Btn sm variant="ghost" onClick={() => { setAddingItem(null); setNewItemText(""); }}>Cancelar</Btn>
                  </div>
                ) : (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddingItem(group)}>+ Adicionar item</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {anteriores}
    </div>
  );
}
